import { groupBy } from 'lodash';
import { FieldArrayWithId, FieldValues } from 'react-hook-form';

import {
  DuplicatedFieldGroupWithoutArrayFieldFullPath,
  RepeaterInputShape,
} from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.types';
import { ArrayFieldNameFromFormShape } from '@/types/react-hook-form';
import { diagnostics } from '@/utils/diagnostics';

export const DELETE_ROW_BUTTON_WIDTH_PX = 50;

/**
 * Given the fields to display in a repeater, group the fields by the idField,
 * and transform the fields into an array where duplicates are grouped in one
 * object. This is used to display a group of duplicate fields in the UI.
 *
 * @param fields
 * @param idField
 * @param fieldArrayKeyName
 * @param shouldGroupDuplicates
 */
export function groupDuplicateFields<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>(
  fields: FieldArrayWithId<
    FormShape,
    ArrayFieldNameFromFormShape<FormShape>,
    FieldArrayKeyName
  >[],
  idField: keyof RepeaterInputShape<FormShape>,
  fieldArrayKeyName: FieldArrayKeyName,
  shouldGroupDuplicates: boolean
): DuplicatedFieldGroupWithoutArrayFieldFullPath<
  FormShape,
  FieldArrayKeyName
>[] {
  if (!shouldGroupDuplicates) {
    // If we don't need to group duplicates, so we can just return the fields
    // with each in their own "group".
    return fields.map((field, index) => ({
      // NOTE: The id here is the idField, not the react-hook-form's auto-generated id.
      id: (field as RepeaterInputShape<FormShape>)[idField],
      existingFieldWithIndex: { index, field },
    }));
  }

  // Group the fields by the idField to find duplicates
  const grouped = groupBy(fields, idField);

  // Within each group, there should be at most 2 fields: the existing field,
  // and the suggested field.
  const groups: DuplicatedFieldGroupWithoutArrayFieldFullPath<
    FormShape,
    FieldArrayKeyName
  >[] = Object.entries(grouped)
    .filter(([id]) => !!id) // Filter out items where the idField is empty
    .map(([id, fieldsInGroup]) => {
      const group: DuplicatedFieldGroupWithoutArrayFieldFullPath<
        FormShape,
        FieldArrayKeyName
      > = {
        // NOTE: The id here is the idField, not the react-hook-form's auto-generated id.
        id,
      };

      fieldsInGroup.forEach((field) =>
        assignFieldInGroup(
          field as RepeaterInputShape<FormShape>,
          group,
          fields,
          fieldArrayKeyName
        )
      );

      return group;
    });

  // Handle empty items here. They'll be appended to the end of the array, each
  // in their own group.
  const emptyGroups = (grouped[''] || []).map((field) => {
    const group: DuplicatedFieldGroupWithoutArrayFieldFullPath<
      FormShape,
      FieldArrayKeyName
    > = {
      // NOTE: The id here is the idField, not the react-hook-form's auto-generated id.
      id: '',
    };
    assignFieldInGroup(
      field as RepeaterInputShape<FormShape>,
      group,
      fields,
      fieldArrayKeyName
    );
    return group;
  });

  return [...groups, ...emptyGroups];
}

function assignFieldInGroup<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>(
  field: RepeaterInputShape<FormShape>,
  group: DuplicatedFieldGroupWithoutArrayFieldFullPath<
    FormShape,
    FieldArrayKeyName
  >,
  fields: FieldArrayWithId<
    FormShape,
    ArrayFieldNameFromFormShape<FormShape>,
    FieldArrayKeyName
  >[],
  fieldArrayKeyName: FieldArrayKeyName
) {
  function findIndexPredicate(
    f: FieldArrayWithId<
      FormShape,
      ArrayFieldNameFromFormShape<FormShape>,
      FieldArrayKeyName
    >
  ) {
    const fieldElement = f as RepeaterInputShape<FormShape>;
    return (
      fieldElement[fieldArrayKeyName] === field[fieldArrayKeyName] &&
      fieldElement.isSuggestion === field.isSuggestion
    );
  }

  const index = fields.findIndex((f) => findIndexPredicate(f));
  if (index === -1) {
    // This should not happen, but if it does, we want to know.
    diagnostics.error(
      "Unable to find the index of the field in the form's array field",
      new Error(
        "Unable to find the index of the field in the form's array field"
      )
    );
    return;
  }

  if (field.isSuggestion) {
    group.suggestedFieldWithIndex = { index, field };
  } else {
    group.existingFieldWithIndex = { index, field };
  }
}
